const PREFIX = 'resunize_app_promo_';

export const SESSION_REASON_KEY = `${PREFIX}session_reason`;

/** @typedef {'register' | 'login'} PromoSessionReason */

export function firstPromoCompletedKey(userId) {
  return `${PREFIX}first_done_${userId}`;
}

export function isFirstPromoCompleted(userId) {
  if (!userId) return true;
  return localStorage.getItem(firstPromoCompletedKey(userId)) === '1';
}

export function markFirstPromoCompleted(userId) {
  if (!userId) return;
  localStorage.setItem(firstPromoCompletedKey(userId), '1');
}

/** @param {PromoSessionReason} reason */
export function setSessionPromoReason(reason) {
  sessionStorage.setItem(SESSION_REASON_KEY, reason);
}

export function peekSessionPromoReason() {
  return sessionStorage.getItem(SESSION_REASON_KEY);
}

export function clearSessionPromoReason() {
  sessionStorage.removeItem(SESSION_REASON_KEY);
}

/** Survives React Strict Mode remount: holds 'register' | 'login' until first promo is dismissed. */
export function firstPromoLockKey(userId) {
  return `${PREFIX}first_lock_${userId}`;
}

export function peekFirstPromoLock(userId) {
  if (!userId) return null;
  const v = sessionStorage.getItem(firstPromoLockKey(userId));
  return v === 'register' || v === 'login' ? v : null;
}

export function setFirstPromoLock(userId, reason) {
  if (!userId || (reason !== 'register' && reason !== 'login')) return;
  sessionStorage.setItem(firstPromoLockKey(userId), reason);
}

export function clearFirstPromoLock(userId) {
  if (!userId) return;
  sessionStorage.removeItem(firstPromoLockKey(userId));
}

function randomLastKey(userId) {
  return `${PREFIX}random_last_${userId}`;
}

const RANDOM_MIN_GAP_MS = 48 * 60 * 60 * 1000;
const RANDOM_CHANCE = 0.12;

export function shouldShowRandomPromo(userId) {
  if (!userId) return false;
  const last = parseInt(localStorage.getItem(randomLastKey(userId)) || '0', 10);
  if (Number.isNaN(last)) return Math.random() < RANDOM_CHANCE;
  if (Date.now() - last < RANDOM_MIN_GAP_MS) return false;
  return Math.random() < RANDOM_CHANCE;
}

export function markRandomPromoShown(userId) {
  if (!userId) return;
  localStorage.setItem(randomLastKey(userId), String(Date.now()));
}

export function isAuthenticatedAppPath(pathname) {
  if (!pathname) return false;
  return (
    pathname.startsWith('/shadchan/') ||
    pathname.startsWith('/single/') ||
    pathname.startsWith('/parent/') ||
    pathname === '/messages' ||
    pathname.startsWith('/my-resumes') ||
    pathname.startsWith('/subscription')
  );
}

/** Landing-page bottom bar: hide after dismiss, show again after TTL (repeat visitors). */
const LANDING_BAR_DISMISSED_AT_KEY = `${PREFIX}landing_bar_dismissed_at`;
const LANDING_BAR_DISMISS_TTL_MS = 5 * 24 * 60 * 60 * 1000;

export function shouldShowLandingAppDownloadBar() {
  const raw = localStorage.getItem(LANDING_BAR_DISMISSED_AT_KEY);
  if (!raw) return true;
  const t = parseInt(raw, 10);
  if (Number.isNaN(t)) return true;
  return Date.now() - t > LANDING_BAR_DISMISS_TTL_MS;
}

export function markLandingAppDownloadBarDismissed() {
  localStorage.setItem(LANDING_BAR_DISMISSED_AT_KEY, String(Date.now()));
}

export const APP_STORE_URL = 'https://apps.apple.com/us/app/resunize/id6764478590';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.resunize.app188864&pcampaignid=web_share';
